import React, { useMemo, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME,
  TABLE_TOOLBAR_BUTTON_CLASSNAME,
  TABLE_TOOLBAR_LEADING_ICON_CLASSNAME,
} from "@src/lib/utils/tableToolbarChrome";
import { cn } from "@src/lib/utils/cn";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import TableColumnsIcon from "@src/lib/icons/18px/table-columns";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";

interface ColumnSelectorProps<Column extends string> {
  allColumns: Column[];
  selectedColumns: Column[];
  onSelectionChange: (selectedColumns: Column[]) => void;
  columnTitles?: Record<Column, string>;
  alwaysVisibleColumns?: Column[];
  defaultSelectedColumns: Column[];
  className?: string;
}

export function ColumnSelector<Column extends string>({
  allColumns,
  selectedColumns,
  onSelectionChange,
  columnTitles,
  alwaysVisibleColumns = [],
  defaultSelectedColumns,
  className,
}: ColumnSelectorProps<Column>): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectableColumns = allColumns.filter(
    (col) => !alwaysVisibleColumns.includes(col),
  );

  const options = useMemo(
    () =>
      selectableColumns.map((column) => ({
        label: columnTitles?.[column] || column,
        value: column,
      })),
    [selectableColumns, columnTitles],
  );

  const selectedSelectableColumns = selectedColumns.filter((col) =>
    selectableColumns.includes(col),
  );

  const handleSelect = (value: string) => {
    const column = value as Column;
    const newSelectedColumns = selectedColumns.includes(column)
      ? selectedColumns.filter((col) => col !== column)
      : [...selectedColumns, column];
    onSelectionChange(newSelectedColumns);
  };

  const clearAll = () => {
    onSelectionChange([...defaultSelectedColumns]);
    setSearchValue("");
  };

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, searchValue]);

  const toggleAll = () => {
    if (selectedSelectableColumns.length === selectableColumns.length) {
      clearAll();
    } else {
      onSelectionChange([...alwaysVisibleColumns, ...selectableColumns]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="md"
          role="combobox"
          aria-expanded={open}
          aria-label="Columns"
          className={cn(TABLE_TOOLBAR_BUTTON_CLASSNAME, className)}
        >
          <TableColumnsIcon className={TABLE_TOOLBAR_LEADING_ICON_CLASSNAME} />
          Columns
          <ChevronDownIcon className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search columns..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="select-all"
                onSelect={() => toggleAll()}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded border",
                    selectedSelectableColumns.length ===
                      selectableColumns.length
                      ? "bg-primary border-primary"
                      : "border-muted-foreground",
                  )}
                >
                  {selectedSelectableColumns.length ===
                    selectableColumns.length && (
                    <CheckmarkIcon className="size-3 text-white" />
                  )}
                </div>
                <span>Select All</span>
              </CommandItem>
              {filteredOptions.map((option) => {
                const isSelected = selectedColumns.includes(
                  option.value as Column,
                );
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 min-w-4 items-center justify-center rounded border",
                        isSelected
                          ? "bg-primary border-primary"
                          : "border-muted-foreground",
                      )}
                    >
                      {isSelected && (
                        <CheckmarkIcon className="size-3 text-white" />
                      )}
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
